import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AssignedToFilter } from "./AssignedToFilter";
import { CreatedByFilter } from "./CreatedByFilter";
import { TagsFilter } from "./TagsFilter";
import type { FilterDescriptor } from "@/lib/erp";
import { useLanguage } from "@/i18n/useLanguage";

type TranslationModule = Record<string, string>;

interface AdvancedFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: FilterDescriptor[];
  onChange: (filters: FilterDescriptor[]) => void;
  translations?: TranslationModule;
}

export function AdvancedFilterModal({
  open,
  onOpenChange,
  filters,
  onChange,
  translations,
}: AdvancedFilterModalProps) {
  const { language } = useLanguage();
  
  // Separate filters by section
  const [assignedToFilters, setAssignedToFilters] = useState<FilterDescriptor[]>([]);
  const [createdByFilters, setCreatedByFilters] = useState<FilterDescriptor[]>([]);
  const [tagsFilters, setTagsFilters] = useState<FilterDescriptor[]>([]);

  // Reset section filters when modal opens or when filters change externally
  useEffect(() => {
    if (open) {
      // Separate filters by section
      const assignedTo = filters.filter(
        (f) => f._metadata?.section === "assigned_to"
      );
      const createdBy = filters.filter(
        (f) => f._metadata?.section === "created_by"
      );
      const tags = filters.filter(
        (f) => f._metadata?.section === "tags"
      );

      setAssignedToFilters(assignedTo);
      setCreatedByFilters(createdBy);
      setTagsFilters(tags);
    }
  }, [open, filters]);

  const handleApply = () => {
    // Combine all sections
    const combinedFilters = [
      ...assignedToFilters,
      ...createdByFilters,
      ...tagsFilters,
    ];
    onChange(combinedFilters);
    onOpenChange(false);
  };

  const handleClearFilters = () => {
    setAssignedToFilters([]);
    setCreatedByFilters([]);
    setTagsFilters([]);
    onChange([]);
    onOpenChange(false);
  };

  const t = translations || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange} key={language}>
      <DialogContent className="max-w-[680px] max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden">
        {/* Top fade-out gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
            <DialogHeader className="px-10 pt-20 pb-4 text-center">
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
                {t.advancedFilters || "Advanced Filters"}
              </DialogTitle>
              <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
                {t.filterByAssignedCreatedTags || "Filter by assigned users, creators, and tags"}
              </DialogDescription>
            </DialogHeader>
            <div className="pb-8 px-10 pt-2">
              <div className="flex justify-center">
                <div className="w-full max-w-3xl space-y-8">
                  {/* Assigned To Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-[17px] font-semibold text-gray-900 dark:text-gray-100">
                        {t.assignedTo || "Assigned To"}
                      </h3>
                    </div>
                    <AssignedToFilter
                      value={assignedToFilters}
                      onChange={setAssignedToFilters}
                      translations={translations}
                    />
                  </div>

                  <Separator className="bg-gray-200 dark:bg-gray-700" />

                  {/* Created By Section */}
                  <div className="space-y-4">
                    <h3 className="text-[17px] font-semibold text-gray-900 dark:text-gray-100">
                      {t.createdBy || "Created By"}
                    </h3>
                    <CreatedByFilter
                      value={createdByFilters}
                      onChange={setCreatedByFilters}
                      translations={translations}
                    />
                  </div>

                  <Separator className="bg-gray-200 dark:bg-gray-700" />

                  {/* Tags Section */}
                  <div className="space-y-4">
                    <h3 className="text-[17px] font-semibold text-gray-900 dark:text-gray-100">
                      {t.tags || "Tags"}
                    </h3>
                    <TagsFilter
                      value={tagsFilters}
                      onChange={setTagsFilters}
                      translations={translations}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bottom fade-out gradient */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
          </div>
          
          {/* Sticky Footer with Buttons */}
          <div className="flex flex-col gap-3 px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              onClick={handleApply}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white px-7 py-3.5 h-auto rounded-md shadow-sm"
            >
              {t.applyFilters || "Apply Filters"}
            </Button>
            <Button
              variant="outline"
              onClick={handleClearFilters}
              className="w-full text-[17px] font-semibold border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100 px-7 py-3.5 h-auto rounded-md"
            >
              {t.clearFilters || "Clear Filters"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

